import Service, { inject as service } from '@ember/service';
import environment from 'garaje/config/environment';
import type { Attributes, ComponentClass, FunctionComponent } from 'react';
import React from 'react';
import type { Root } from 'react-dom/client';
import { createRoot } from 'react-dom/client';
import { type ReactRichComponentsConfig } from 'reactRichComponents/config';

import type ReactAnalytics from './react-analytics';
import type SplitioService from './splitio';
import type StateService from './state';

export default class ReactRendererService extends Service {
  @service declare reactAnalytics: ReactAnalytics;
  @service declare splitio: SplitioService;
  @service declare state: StateService;

  async render<P extends object>(
    component: FunctionComponent<P> | ComponentClass<P>,
    elementId: string,
    props?: Attributes & P,
  ): Promise<Root> {
    try {
      await import('reactRichComponents/styles');
    } catch (_: unknown) {
      this.reactAnalytics.trackMonitoring('RRC_STYLE_FETCH_FAILED');
    }

    const { reactRichComponentsConfig } = await import('reactRichComponents/config');
    this.setEnvironment(reactRichComponentsConfig);
    const renderedComponent = React.createElement<P>(component, props, null);

    const element = document.getElementById(elementId);

    if (!element) {
      throw new Error(`Element with id ${elementId} was not found.`);
    }

    reactRichComponentsConfig.analytics = this.reactAnalytics;
    reactRichComponentsConfig.splitClient = this.splitio.getSdk('company');
    reactRichComponentsConfig.serviceUrls.envoyWeb = '/a/visitors/';
    reactRichComponentsConfig.serviceUrls.federatedGraphQL = '/a/graphql_federated';
    reactRichComponentsConfig.googleMapsApiKey = (
      environment as unknown as { googleMapsApiKey: string }
    ).googleMapsApiKey;
    reactRichComponentsConfig.featureConfig = {
      canAccessMultiPartApprovals: this.state.features?.canAccessMultiPartApprovals ?? false,
    };

    const root = createRoot(element);

    // Issue with React's inbuilt typing, so we need to force an any assignment here
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any
    root.render(renderedComponent as any);

    return root;
  }

  rerender<P extends object>(
    component: FunctionComponent<P> | ComponentClass<P>,
    root: Root,
    props?: Attributes & P,
  ): void {
    const renderedComponent = React.createElement<P>(component, props, null);

    // Issue with React's inbuilt typing, so we need to force an any assignment here
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any
    root.render(renderedComponent as any);
  }

  unmount(root: Root): void {
    root.unmount();
  }

  private setEnvironment(reactRichComponentsConfig: ReactRichComponentsConfig): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const emberEnv = (environment as unknown as { environment: string }).environment;
    if (emberEnv === 'development') {
      reactRichComponentsConfig.setEnvironment('dev');
    } else if (emberEnv === 'production') {
      reactRichComponentsConfig.setEnvironment('production');
    } else {
      reactRichComponentsConfig.setEnvironment('staging');
    }
  }
}
